import { compact, differenceBy, flatten, get, isArray, isEmpty, map, times } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Label, ModalBody, ModalFooter } from "reactstrap";
import { DetailedErrorAlert } from "../components/DetailedErrorAlert";
import { LoadingButton } from "../components/LoadingButton";
import { api, redirectTo } from "../util/api";
import { SplitHistory } from "./SplitHistory";
import { SplitPicker } from "./SplitPicker";

function makeTestOption(option) {
  return {
    label: option.name,
    value: option.id,
  };
}

const makeSplit = () => ({ tests: "" });

const getTestOptionsBySplit = (splits, allTestOptions) => {
  const optionsBySplit = splits.map(() => {
    const usedTests = flatten(compact(splits.map((s) => (isArray(s.tests) ? s.tests : null))));
    return differenceBy(allTestOptions, usedTests, "value");
  });

  return optionsBySplit;
};

export const RequestSplitter = ({ caseId }) => {
  //
  // State
  //

  const [splits, setSplits] = useState([makeSplit(), makeSplit()]);
  const [allTestOptions, setAllTestOptions] = useState([]);
  const [splitHistory, setSplitHistory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  //
  // Handlers
  //

  const fetchTestOptions = useCallback(
    async (mounted = true) => {
      try {
        const testUrl = `/operations/prior_authorize_requests/${caseId}/test_options`;
        const { data } = await api.get(testUrl);

        if (mounted) {
          setAllTestOptions(data.map(makeTestOption));
        }
      } catch (e) {
        if (mounted) {
          setError(e);
          setAllTestOptions([]);
        }
      }
    },
    [caseId, setAllTestOptions, setError]
  );

  const fetchSplitFromParent = useCallback(
    async (mounted = true) => {
      try {
        const parentSplitUrl = `/operations/prior_authorize_requests/${caseId}/split_history`;
        const { data } = await api.get(parentSplitUrl);

        if (mounted) {
          setSplitHistory(data);
        }
      } catch (e) {
        if (mounted) {
          setError(e);
          setSplitHistory(null);
        }
      }
    },
    [caseId, setSplitHistory, setError]
  );

  const handleSplitCountChange = useCallback(
    (e) => {
      const count = Math.max(e.target.valueAsNumber, 2);
      const oldSplits = [...splits];
      const newSplits = times(count).map(() => oldSplits.shift() || makeSplit());

      setSplits(newSplits);
    },
    [setSplits, splits]
  );

  const handleSplitUpdate = (updatedSplit, idx) => {
    const newSplits = [...splits];
    newSplits[idx] = updatedSplit;
    setSplits(newSplits);
  };

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setError(null);

    try {
      const payload = map(splits, (s) => ({
        ...s,
      }));

      const { data } = await api.post(`/operations/prior_authorize_requests/${caseId}/splits`, { split_attribute: payload });

      // set the target explicitly to avoid route mismatching
      redirectTo(`/operations/cases/${data.value[0]}`);
    } catch (e) {
      setError(e);
    } finally {
      setSubmitting(false);
    }
  }, [splits, caseId]);

  //
  // Effects
  //

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => Promise.all([fetchTestOptions(mounted), fetchSplitFromParent(mounted)]);

    fetchAll();

    return () => void (mounted = false);
  }, [fetchTestOptions, fetchSplitFromParent]);

  //
  // Calculated State
  //

  const hasSplitHistory = useMemo(() => !isEmpty(get(splitHistory, "parent")) || !isEmpty(get(splitHistory, "children")), [splitHistory]);

  const unsplittable = useMemo(() => !get(splitHistory, "is_splittable", true), [splitHistory]);
  const allSplitsHaveTest = useMemo(() => !splits.some((x) => x.tests == "" || x.tests == null), [splits]);
  const testOptions = useMemo(() => getTestOptionsBySplit(splits, allTestOptions), [splits, allTestOptions]);
  const remainingTests = useMemo(() => testOptions[0]?.map((o) => o.label), [testOptions]);
  const hasRemainingTests = remainingTests.length > 0;

  return (
    <>
      <div className="modal-header">
        <h5 className="modal-title">Split Request</h5>

        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <ModalBody>
        <DetailedErrorAlert error={error} />

        {hasSplitHistory && <SplitHistory {...splitHistory} />}

        <div className="mb-3 d-flex flex-row align-items-center">
          Split Into{" "}
          <Input
            type="number"
            name="split-count"
            id="split-count"
            className="mx-2"
            value={splits?.length || 2}
            onChange={handleSplitCountChange}
            disabled={unsplittable}
            style={{ width: 80, display: "inline-block" }}
          />{" "}
          Cases
        </div>

        <hr className="my-4" />

        {hasRemainingTests > 0 && (
          <div className="d-flex flex-row flex-wrap align-items-center mb-3">
            <Label>Unused tests:&nbsp;</Label>
            {map(remainingTests, (t) => (
              <span className="token mx-1 mb-1" key={t}>
                {t}
              </span>
            ))}
          </div>
        )}

        {hasRemainingTests && <hr className="my-4" />}

        {splits.map((split, idx) => (
          <SplitPicker
            key={idx}
            split={split}
            testOptions={testOptions[idx]}
            disabled={unsplittable}
            onChangeTest={(tests) => handleSplitUpdate({ ...split, tests }, idx)}
          />
        ))}
      </ModalBody>
      <ModalFooter>
        <Button outline color="secondary" data-bs-dismiss="modal">
          Cancel
        </Button>

        <LoadingButton
          color="primary"
          disabled={submitting || unsplittable || !allSplitsHaveTest}
          loading={submitting}
          onClick={handleSubmit}
        >
          Split
        </LoadingButton>
      </ModalFooter>
    </>
  );
};
